<template>
  <div class="leaderboard-page">
    <!-- 横屏模式 -->
    <LandscapeComponent
      v-if="isDataLoaded && mode === 'landscape'"
      :leaderboard-data="leaderboardData"
      :current-month="currentMonth"
      :current-week-range="currentWeekRange"
      :weekly-team-data-by-amount="weeklyTeamDataByAmount"
      :monthly-team-data-by-amount="monthlyTeamDataByAmount"
      :weekly-individual-data-by-amount="weeklyIndividualDataByAmount"
      :monthly-individual-data-by-amount="monthlyIndividualDataByAmount"
      :weekly-team-data-by-rate="weeklyTeamDataByRate"
      :monthly-team-data-by-rate="monthlyTeamDataByRate"
      :weekly-individual-data-by-rate="weeklyIndividualDataByRate"
      :monthly-individual-data-by-rate="monthlyIndividualDataByRate"
      :auto-scroll-interval="autoScrollInterval"
    />

    <!-- 竖屏模式 -->
    <PortraitComponent
      v-else-if="isDataLoaded && mode === 'portrait'"
      :leaderboard-data="leaderboardData"
      :current-month="currentMonth"
      :current-week-range="currentWeekRange"
      :weekly-team-data-by-amount="weeklyTeamDataByAmount"
      :monthly-team-data-by-amount="monthlyTeamDataByAmount"
      :weekly-individual-data-by-amount="weeklyIndividualDataByAmount"
      :monthly-individual-data-by-amount="monthlyIndividualDataByAmount"
      :weekly-team-data-by-rate="weeklyTeamDataByRate"
      :monthly-team-data-by-rate="monthlyTeamDataByRate"
      :weekly-individual-data-by-rate="weeklyIndividualDataByRate"
      :monthly-individual-data-by-rate="monthlyIndividualDataByRate"
      :auto-scroll-interval="autoScrollInterval"
    />

    <!-- 加载状态 -->
    <div v-else class="loading-overlay">
      <div class="loading-spinner"></div>
      <div class="loading-text">加载中...</div>
    </div>
  </div>
</template>

<script>
import { Vue, Component } from 'vue-property-decorator';
import { request } from 'beans-admin-plugin'
import LandscapeComponent from './landscape.vue'
import PortraitComponent from './portrait.vue'
import moment from 'moment'

@Component({
  components: {
    LandscapeComponent,
    PortraitComponent
  }
})
export default class Page extends Vue {
  leaderboardData = {
    weekly_team: { rankings: [] },
    monthly_team: { rankings: [] },
    weekly_individual: { rankings: [] },
    monthly_individual: { rankings: [] }
  }

  currentMonth = moment()
  mode = 'landscape' // 'landscape' | 'portrait' - 自动根据窗口大小判断
  isDataLoaded = false // 数据加载状态

  // 自动滚动相关配置
  autoScrollInterval = 60 // 自动滚动间隔时间（秒）

  get currentWeekRange() {
    const weeks = this.getWeeksInMonth()
    const today = moment()
    const currentWeek = weeks.find(week =>
      today.isSameOrAfter(week.start, 'day') && today.isSameOrBefore(week.end, 'day')
    )

    if (currentWeek) {
      return {
        start: currentWeek.start.format('YYYY年MM月DD日'),
        end: currentWeek.end.format('DD日')
      }
    }

    // 如果今天不在任何周内，使用第一周
    if (weeks.length > 0) {
      return {
        start: weeks[0].start.format('YYYY年MM月DD日'),
        end: weeks[0].end.format('DD日')
      }
    }

    return {
      start: today.format('YYYY年MM月DD日'),
      end: today.format('DD日')
    }
  }

  // 按下单金额排序的数据
  get weeklyTeamDataByAmount() {
    return this.processLeaderboardData(this.leaderboardData.weekly_team.rankings, 'team', 'amount')
  }

  get monthlyTeamDataByAmount() {
    return this.processLeaderboardData(this.leaderboardData.monthly_team.rankings, 'team', 'amount')
  }

  get weeklyIndividualDataByAmount() {
    return this.processLeaderboardData(this.leaderboardData.weekly_individual.rankings, 'individual', 'amount')
  }

  get monthlyIndividualDataByAmount() {
    return this.processLeaderboardData(this.leaderboardData.monthly_individual.rankings, 'individual', 'amount')
  }

  // 按目标达成率排序的数据
  get weeklyTeamDataByRate() {
    return this.processLeaderboardData(this.leaderboardData.weekly_team.rankings, 'team', 'rate')
  }

  get monthlyTeamDataByRate() {
    return this.processLeaderboardData(this.leaderboardData.monthly_team.rankings, 'team', 'rate')
  }

  get weeklyIndividualDataByRate() {
    return this.processLeaderboardData(this.leaderboardData.weekly_individual.rankings, 'individual', 'rate')
  }

  get monthlyIndividualDataByRate() {
    return this.processLeaderboardData(this.leaderboardData.monthly_individual.rankings, 'individual', 'rate')
  }

  processLeaderboardData(rankings, type, sortType = 'amount') {
    if (!rankings || rankings.length === 0) return { headers: [], rows: [] }

    // 计算排名并排序
    const sortedRankings = [...rankings]
      .sort((a, b) => {
        if (sortType === 'rate') {
          // 按目标达成率排序
          const rateA = this.calculateAchievementRateValue(a.order_actual, a.order_target)
          const rateB = this.calculateAchievementRateValue(b.order_actual, b.order_target)
          if (rateB !== rateA) {
            return rateB - rateA // 按达成率降序
          }
          return a.id - b.id // 达成率相同按id升序
        } else {
          // 按下单金额排序
          const actualA = parseFloat(a.order_actual) || 0
          const actualB = parseFloat(b.order_actual) || 0
          if (actualB !== actualA) {
            return actualB - actualA // 按业绩降序
          }
          return a.id - b.id // 业绩相同按id升序
        }
      })

    // 计算排名（支持并列）
    const rankedData = []
    let currentRank = 1

    for (let i = 0; i < sortedRankings.length; i++) {
      const item = sortedRankings[i]
      const prevItem = sortedRankings[i - 1]

      // 如果当前项的业绩与前一项不同，更新排名
      if (i > 0 && parseFloat(item.order_actual) !== parseFloat(prevItem.order_actual)) {
        currentRank = i + 1
      }

      rankedData.push({
        ...item,
        rank: currentRank
      })
    }

    // 只显示前3名和后3名
    const displayData = []
    if (rankedData.length <= 6) {
      displayData.push(...rankedData)
    } else {
      displayData.push(...rankedData.slice(0, 3)) // 前3名
      displayData.push(...rankedData.slice(-3))    // 后3名
    }

    const headers = type === 'team'
      ? ['排名', '所属队伍', '达成率', '销售业绩']
      : ['排名', '姓名', '达成率', '销售业绩']

    const rows = displayData.map(item => [
      item.rank.toString(),
      item.name,
      this.calculateAchievementRate(item.order_actual, item.order_target),
      this.formatCurrency(item.order_actual)
    ])

    return { headers, rows }
  }

  calculateAchievementRate(actual, target) {
    const actualNum = parseFloat(actual) || 0
    const targetNum = parseFloat(target) || 0

    if (targetNum === 0) return '0%'

    const rate = (actualNum / targetNum * 100)

    // 保留2位小数后的结果
    const formattedRate = parseFloat(rate.toFixed(2))

    // 如果保留2位小数后结果为0，直接返回0%
    if (formattedRate === 0) return '0%'

    // 返回格式化的百分比
    return `${formattedRate}%`
  }

  formatCurrency(amount) {
    const num = parseFloat(amount) || 0
    if (num === 0) return '0'

    // 转换为万为单位
    const wanAmount = num / 10000
    return `${wanAmount.toLocaleString()}万`
  }

  calculateAchievementRateValue(actual, target) {
    const actualNum = parseFloat(actual) || 0
    const targetNum = parseFloat(target) || 0

    if (targetNum === 0) return 0

    return actualNum / targetNum * 100
  }

  toggleSortType() {
    this.currentSortType = this.currentSortType === 'amount' ? 'rate' : 'amount'
  }

  // 根据窗口大小自动判断显示模式
  updateModeBasedOnWindowSize() {
    const windowWidth = window.innerWidth
    const windowHeight = window.innerHeight
    this.mode = windowWidth > windowHeight ? 'landscape' : 'portrait'
  }

  // 窗口大小变化监听器
  handleResize() {
    this.updateModeBasedOnWindowSize()
  }


  // 复制自performance_goals/week/table.vue的getWeeksInMonth方法
  getWeeksInMonth() {
    const startOfMonth = this.currentMonth.clone().startOf('month')
    const endOfMonth = this.currentMonth.clone().endOf('month')
    const weeks = []

    // 获取月初第一天是星期几 (moment中: 0=周日, 1=周一, ..., 6=周六)
    const firstDayOfWeek = startOfMonth.day()

    // 计算第一周的开始和结束
    let weekStart = startOfMonth.clone()
    let weekEnd

    if (firstDayOfWeek === 0) {
      // 如果1号是周日，第一周只有1天
      weekEnd = startOfMonth.clone()
    } else {
      // 第一周从1号到当周的周日
      // 计算到当周周日需要的天数：7 - firstDayOfWeek
      const daysToSunday = 7 - firstDayOfWeek
      weekEnd = startOfMonth.clone().add(daysToSunday, 'days')

      // 如果周日超出了当月，则第一周到月末
      if (weekEnd.isAfter(endOfMonth, 'day')) {
        weekEnd = endOfMonth.clone()
      }
    }

    weeks.push({
      start: weekStart.clone(),
      end: weekEnd.clone()
    })

    // 计算后续完整周：从周一到周日
    let currentWeekStart = weekEnd.clone().add(1, 'day')

    while (currentWeekStart.isSameOrBefore(endOfMonth, 'day')) {
      let currentWeekEnd = currentWeekStart.clone().add(6, 'days') // 从周一加6天到周日

      // 如果周日超出当月，截止到月末
      if (currentWeekEnd.isAfter(endOfMonth, 'day')) {
        currentWeekEnd = endOfMonth.clone()
      }

      weeks.push({
        start: currentWeekStart.clone(),
        end: currentWeekEnd.clone()
      })

      // 移动到下一周的周一
      currentWeekStart = currentWeekEnd.clone().add(1, 'day')
    }

    return weeks
  }

  // get /admin_api/v1/leaderboard
  async fetchData() {
    try {
      const data = await request.get('leaderboard/rankings')
      this.leaderboardData = data
    } catch (error) {
      console.error('Failed to fetch leaderboard data:', error)
    }
  }


  async getApplicationCurrent() {
    try {
      const data = await request.get('applications/current');
      this.autoScrollInterval = data.leaderboard_scroll_interval || 60;
    } catch (error) {
      console.error('Failed to fetch application config:', error);
      this.autoScrollInterval = 60; // 使用默认值60秒
    }
  }

  async mounted() {
    await Promise.all([
      this.fetchData(),
      this.getApplicationCurrent(),
    ])

    // 数据加载完成
    this.isDataLoaded = true

    // 初始化时设置显示模式
    this.updateModeBasedOnWindowSize()

    // 添加窗口大小变化监听器
    window.addEventListener('resize', this.handleResize)
  }

  beforeDestroy() {
    // 移除窗口大小变化监听器
    window.removeEventListener('resize', this.handleResize)
  }
}

export const route = [
  {
    path: '/leaderboard',
    name: 'leaderboard',
    component: Page,
    meta: {
      can: 'teams.read_leaderboard',
      title: '战队龙虎榜'
    }
  },
]
</script>

<style lang="scss" scoped>
.leaderboard-page {
  height: 100%;
  width: 100%;
}

// Loading 效果样式 - 与 rank.vue 保持一致
.loading-overlay {
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: rgba(255, 255, 255, 0.9);
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  border-radius: 1.5vh;
  z-index: 10;

  .loading-spinner {
    width: 4vh;
    height: 4vh;
    border: 0.3vh solid #f3f3f3;
    border-top: 0.3vh solid #fccb9a;
    border-radius: 50%;
    animation: spin 1s linear infinite;
    margin-bottom: 1vh;
  }

  .loading-text {
    color: #4d2b07;
    font-size: 1.4vh;
    font-weight: 600;
  }
}

// 旋转动画
@keyframes spin {
  0% { transform: rotate(0deg); }
  100% { transform: rotate(360deg); }
}
</style>
